import { useMemo } from 'react';
import GraphIcon from '../../assets/icons/pulse-alert.svg?react';
import MachineIcon from '../../assets/icons/machine-alarm.svg?react';
import {
  NotificationEvent,
  NotificationMember,
  ThresholdMode,
  TriggerMode,
  useGetAlarmQuery,
  useGetMachineQuery,
} from '../../gql/generated';
import { cn } from '../../utils/className';
import {
  formatMetricName,
  formatThreshold,
  formatTrigger,
  formatUnit,
  isMachineAlarmSignal,
} from '../../utils/formatMetric';
import { MachineHistoryGraph } from '../dialogs/MachineHistoryGraph';
import { InfoCard } from '../SidePanel';

type TimeRange = '24h' | '7d' | '6m' | '1y';

/** A graph opens on a window that holds the moment the alarm was sent. */
function calculateInitialTimeRange(alarmTime: number | undefined): TimeRange {
  if (!alarmTime) return '24h';

  const ageMs = Date.now() - alarmTime;

  const ONE_DAY = 24 * 60 * 60 * 1000;
  const SEVEN_DAYS = 7 * ONE_DAY;
  const SIX_MONTHS = 180 * ONE_DAY;

  if (ageMs <= ONE_DAY) return '24h';
  if (ageMs <= SEVEN_DAYS) return '7d';
  if (ageMs <= SIX_MONTHS) return '6m';
  return '1y';
}

interface NotificationDetailContentProps {
  notification: NotificationEvent;
  /** The member alarm to show. A send for one alarm shows that alarm. */
  member?: NotificationMember;
  className?: string;
}

/**
 * What one alarm looks like: the signal around the moment it was sent, the
 * metric it watches and the logic that fired it.
 */
export function NotificationDetailContent({
  notification,
  member,
  className,
}: NotificationDetailContentProps) {
  const alarmSource = member ?? notification;

  const { data: machineData } = useGetMachineQuery({
    variables: { id: notification.machineId ?? '' },
    skip: !notification.machineId,
  });

  const { data: alarmData } = useGetAlarmQuery({
    variables: { id: alarmSource.alarmId ?? '' },
    skip: !alarmSource.alarmId,
  });

  const machine = machineData?.machine;
  const alarm = alarmData?.alarm;

  const triggerModeLabel = useMemo(() => {
    if (!alarm) return null;
    switch (alarm.triggerMode) {
      case TriggerMode.Percentage:
        return 'Percentage';
      case TriggerMode.Period:
        return 'Period';
      case TriggerMode.Spikes:
        return 'Spikes';
      default:
        return null;
    }
  }, [alarm]);

  // Reads the way the alarms table says it
  const triggerValueDisplay = alarm ? formatTrigger(alarm) : null;

  // Reads the way the alarms table says it
  const thresholdDisplay = alarm ? formatThreshold(alarm) : null;

  const metricDisplay = useMemo(() => {
    if (!alarm) return null;
    const signal = alarm.signal || alarm.signalCategory;
    return signal ? formatMetricName(signal) : null;
  }, [alarm]);

  const alarmTriggerTime = useMemo(
    () =>
      notification.timeUtc
        ? new Date(notification.timeUtc).getTime()
        : undefined,
    [notification.timeUtc]
  );

  // Derived at render time: the graph reads the window once on mount, so it
  // must be right on the very first render (the alarm often resolves straight
  // from the Apollo cache).
  const initialTimeRange = useMemo<TimeRange>(
    () => calculateInitialTimeRange(alarmTriggerTime),
    [alarmTriggerTime]
  );

  const alarmThresholds = useMemo(() => {
    if (!alarm) return undefined;

    if (alarm.thresholdMode === ThresholdMode.AboveHigh) {
      return { maximum: alarm.thresholdHigh ?? undefined };
    }
    if (alarm.thresholdMode === ThresholdMode.BelowLow) {
      return { minimum: alarm.thresholdLow ?? undefined };
    }
    if (alarm.thresholdMode === ThresholdMode.OutsideRange) {
      return {
        minimum: alarm.thresholdLow ?? undefined,
        maximum: alarm.thresholdHigh ?? undefined,
      };
    }
    return undefined;
  }, [alarm]);

  // The alarm's machines carry the machineId EDMS reads. The notification
  // names one of them, so the graph shows that machine alone.
  const machineOptions = useMemo(() => {
    const alarmMachines = alarm?.machines || [];

    if (alarmMachines.length > 0) {
      const notificationMachineId = notification.machineId;

      if (notificationMachineId) {
        const matchingMachine = alarmMachines.find(
          (m) => m.id === notificationMachineId
        );

        if (matchingMachine) {
          return [
            {
              id: matchingMachine.machineId || matchingMachine.id || '',
              displayName:
                matchingMachine.displayName ||
                matchingMachine.machineId ||
                matchingMachine.id ||
                'Unknown machine',
            },
          ];
        }
      }

      return alarmMachines
        .map((m) => ({
          id: m.machineId || m.id || '',
          displayName:
            m.displayName || m.machineId || m.id || 'Unknown machine',
        }))
        .filter((m) => m.id);
    }

    if (!machine) return [];
    return [
      {
        id: machine.machineId || machine.id,
        displayName: machine.displayName || machine.machineId || machine.id,
      },
    ];
  }, [alarm, machine, notification.machineId]);

  return (
    <div className={cn('flex flex-col gap-5', className)}>
      {alarm && machineOptions.length > 0 && (
        <div className='flex flex-col gap-3'>
          <MachineHistoryGraph
            header={
              // Same header as the wizard and the alert's Graph tab
              <div className='flex min-w-0 items-center gap-2'>
                {isMachineAlarmSignal({
                  unit: alarm.unit,
                  signalCategory: alarm.signalCategory,
                }) ? (
                  <MachineIcon
                    className='h-5 w-5 shrink-0'
                    aria-hidden='true'
                  />
                ) : (
                  <GraphIcon className='h-5 w-5 shrink-0' aria-hidden='true' />
                )}
                <h3 className='size-p2 text-primary truncate font-bold'>
                  {formatMetricName(alarm.signal)}
                </h3>
              </div>
            }
            machines={machineOptions}
            signalCategory={alarm.signalCategory}
            signal={alarm.signal}
            unit={formatUnit(alarm.unit ?? undefined)}
            alarmThresholds={alarmThresholds}
            alarmTriggerTime={alarmTriggerTime}
            initialTimeRange={initialTimeRange}
          />
        </div>
      )}

      {(!alarm || machineOptions.length === 0) && (
        <div
          className='bg-surface-secondary flex h-55 items-center justify-center
            rounded-lg'
        >
          <span className='text-tertiary size-caption'>
            {!alarm
              ? 'Loading Pulse alert details...'
              : 'No machine data available'}
          </span>
        </div>
      )}

      {alarm && (
        <div className='flex gap-2'>
          <InfoCard
            label='Metric'
            title={metricDisplay || 'Unknown'}
            badge={thresholdDisplay}
          />
          <InfoCard
            label='Trigger logic'
            title={triggerModeLabel || 'Unknown'}
            badge={triggerValueDisplay}
          />
        </div>
      )}

      {!alarm &&
        alarmSource.triggerValues &&
        alarmSource.triggerValues.length > 0 && (
          <div className='flex flex-col gap-4'>
            <span className='size-p2 text-primary font-medium'>
              Trigger Values
            </span>
            <div className='flex flex-col gap-2'>
              {alarmSource.triggerValues.map((tv, idx) => {
                const modeLabel =
                  tv.mode === TriggerMode.Percentage
                    ? '%'
                    : tv.mode === TriggerMode.Period
                      ? ' sec'
                      : tv.mode === TriggerMode.Spikes
                        ? ' spikes'
                        : '';

                return (
                  <div key={idx} className='flex flex-col gap-1'>
                    <span className='size-p2 text-primary'>
                      {tv.value != null ? `${tv.value}${modeLabel}` : '-'}
                    </span>
                    {tv.tags && tv.tags.length > 0 && (
                      <div
                        className='size-caption text-tertiary flex flex-col
                          gap-0.5'
                      >
                        {tv.tags.map((tag, tagIdx) => (
                          <span key={tagIdx}>
                            {tag.key}: {tag.value}
                          </span>
                        ))}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        )}
    </div>
  );
}
